<template>
    <div class="ui-page">
        <ui-page-header :title="form.name || 'Roteiro'">
            <template #title-append>
                <ui-badge set="prospect-trip" :status="form.status" />
                <ui-badge v-if="trip && trip.plan && trip.plan.source" :status="trip.plan.source === 'waybuilder' ? 'active' : 'pending'" :text="sourceLabel" />
            </template>
            <template #subtitle>
                <span :class="{ 'pe-error': saveError }">{{ saveText }}</span>
            </template>
            <v-btn outlined to="/prospeccao/rota"><v-icon left small>mdi-arrow-left</v-icon>Roteiros</v-btn>
            <ui-action-menu label="Google Maps" icon="mdi-google-maps" :disabled="!mapsItems.length" :items="mapsItems" />
            <v-btn v-if="$allowed('edit')" outlined :disabled="!trip" @click="openSuggest"><v-icon left small>mdi-auto-fix</v-icon>Montar automaticamente</v-btn>
            <v-btn v-if="$allowed('prospect.meetings')" color="primary" depressed :disabled="!trip || !stops.length" @click="openGenerate"><v-icon left small>mdi-calendar-multiselect</v-icon>Gerar agendas</v-btn>
        </ui-page-header>

        <div v-if="loading && !trip" class="pe-loading"><v-progress-circular indeterminate size="28" width="3" color="primary"></v-progress-circular></div>

        <template v-if="trip">
            <div class="pe-fields mb-4">
                <v-text-field v-model="form.name" label="Nome do roteiro" dense outlined hide-details class="pe-fields__name" @input="queueSave"></v-text-field>
                <v-select v-model="form.status" :items="statusItems" label="Situação" dense outlined hide-details :menu-props="{ offsetY: true }" style="max-width: 170px;" @change="queueSave"></v-select>
                <v-select v-model="form.uf" :items="ufs" label="UF dos casos" dense outlined hide-details clearable :menu-props="{ offsetY: true }" style="max-width: 130px;" @change="onUfChange"></v-select>
                <v-autocomplete v-model="form.base_id" :items="baseOptions" item-value="id" :item-text="cityText" :search-input.sync="baseSearch"
                    :label="form.travel_mode === 'flight' ? 'Base (cidade do aeroporto)' : 'Cidade-base (saída e volta)'" :disabled="form.travel_mode === 'flight'"
                    dense outlined hide-details no-filter style="max-width: 280px;" prepend-inner-icon="mdi-home-map-marker" no-data-text="Digite o nome da cidade" @change="onBaseChange"></v-autocomplete>
                <v-text-field v-model="form.start_date" type="date" label="Saída (dia 1)" dense outlined hide-details style="max-width: 170px;" @change="queueSave"></v-text-field>
            </div>

            <nav class="pe-steps mb-4" aria-label="Etapas do roteiro">
                <button v-for="(item, index) in steps" :key="item.value" type="button" class="pe-step" :class="{ 'pe-step--active': step === item.value }" @click="setStep(item.value)">
                    <span class="pe-step__number">{{ index + 1 }}</span>
                    <span class="pe-step__text">
                        <strong>{{ item.title }}</strong>
                        <span class="ui-small ui-muted">{{ item.summary }}</span>
                    </span>
                </button>
            </nav>

            <trip-teams v-if="step === 'casos'" />

            <div v-show="step === 'casos' || step === 'rota'">
            <ui-section title="Mapa" class="mb-4" :description="mapDescription">
                <template #actions>
                    <span class="ui-small ui-muted mr-1">Incluir em:</span>
                    <button v-for="team in teams" :key="team.key" type="button" class="pe-chip" :class="{ 'pe-chip--active': team.key === activeTeamKey }" @click="activeTeamKey = team.key">
                        <i :style="{ background: team.color }"></i>{{ team.name }}
                    </button>
                </template>
                <prospect-route-map :base="mapBase" :cities="mapCities" :teams="mapTeams" :active-team="activeMapTeam" :round-trip="form.options.round_trip"
                    :airports="mapAirports" :arrival-airport-id="form.travel_mode === 'flight' ? form.arrival_airport_id : null" :offices="mapOffices"
                    :pois="mapPois" :pois-loading="poisLoading" :pois-error="poisError" @load-pois="loadPois"
                    editable :fit-key="String(form.base_id || '')" @add-cases="addCases" @remove-cases="removeCases" />
                <p class="ui-small ui-muted mt-2 mb-0">Clique numa cidade para incluir ou tirar casos, ou use a seleção por área (retângulo ou laço) para vários de uma vez. Os números seguem a ordem de visita de cada equipe.</p>
            </ui-section>
            </div>

            <trip-cases v-if="step === 'casos'" />

            <template v-if="step === 'viagem'">
                <trip-travel />
                <trip-window />
            </template>

            <div v-if="step === 'rota'" class="pe-grid">
                <trip-agenda />
                <trip-stops />
            </div>

            <trip-review v-if="step === 'revisar'" />

            <div class="pe-step-nav mt-4">
                <v-btn v-if="stepIndex > 0" outlined @click="setStep(steps[stepIndex - 1].value)"><v-icon left small>mdi-arrow-left</v-icon>{{ steps[stepIndex - 1].title }}</v-btn>
                <v-spacer></v-spacer>
                <v-btn v-if="stepIndex < steps.length - 1" color="primary" depressed @click="setStep(steps[stepIndex + 1].value)">{{ steps[stepIndex + 1].title }}<v-icon right small>mdi-arrow-right</v-icon></v-btn>
            </div>
        </template>

        <!-- Montar automaticamente -->
        <v-dialog v-model="suggest.open" max-width="980" scrollable>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Montar automaticamente</h3>
                        <p class="ui-section-description">Grupos de casos pendentes por região, com o melhor jeito de chegar a partir da sede. Usar um grupo troca os casos deste roteiro e ajusta a sede, o modo e a base.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <div class="pe-fields mb-3">
                        <v-select v-model="suggest.uf" :items="ufs" label="UF" dense outlined hide-details clearable :menu-props="{ offsetY: true }" style="max-width: 120px;" @change="loadSuggest"></v-select>
                        <v-select v-model="suggest.max_cases" :items="[4, 6, 8, 10, 12]" label="Casos por roteiro (até)" dense outlined hide-details :menu-props="{ offsetY: true }" style="max-width: 200px;" @change="loadSuggest"></v-select>
                    </div>
                    <div v-if="suggest.loading" class="pe-loading"><v-progress-circular indeterminate size="24" width="3" color="primary"></v-progress-circular></div>
                    <ui-empty v-else-if="!suggest.items.length" text="Nenhum grupo para sugerir com esses filtros." />
                    <div v-else class="pe-suggestions">
                        <prospect-trip-suggestion v-for="item in suggest.items" :key="item.key" :suggestion="item" action-label="Usar este grupo" @use="applySuggestion" />
                    </div>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined @click="suggest.open = false">Fechar</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <!-- Fixar dia e horario -->
        <v-dialog v-model="pin.open" max-width="440">
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Dia e horário combinados</h3>
                        <p class="ui-section-description">{{ pin.case_id ? caseName(pin.case_id) : "" }}. Sem combinado, a plataforma encaixa a visita na janela.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <div class="d-flex" style="gap: 12px;">
                        <v-select v-model="pin.day" :items="pinDayItems" label="Dia" outlined dense hide-details :menu-props="{ offsetY: true }"></v-select>
                        <v-text-field v-model="pin.time" type="time" label="Horário" outlined dense hide-details clearable></v-text-field>
                    </div>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined @click="pin.open = false">Cancelar</v-btn>
                    <v-btn color="primary" depressed @click="savePin">Aplicar</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <!-- Dividir entre equipes -->
        <v-dialog v-model="split.open" max-width="460" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Dividir entre equipes</h3>
                        <p class="ui-section-description">Os casos são separados pela direção a partir da base, com quantidade parecida por equipe. Visitas com dia combinado ou travado não mudam de equipe.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <v-select v-model="split.count" :items="[2, 3, 4, 5, 6, 7, 8]" label="Quantas equipes" outlined dense hide-details :menu-props="{ offsetY: true }" style="max-width: 180px;"></v-select>
                    <p class="ui-small ui-muted mt-3 mb-0">A ordem de cada equipe volta a ser a otimizada. Equipes que faltarem são criadas; defina depois os responsáveis.</p>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="split.saving" @click="split.open = false">Cancelar</v-btn>
                    <v-btn color="primary" depressed :loading="split.saving" @click="submitSplit">Dividir</v-btn>
                </div>
            </v-card>
        </v-dialog>

    </div>
</template>

<script>
import axios from "axios";
import ProspectRouteMap from "@/components/prospect/ProspectRouteMap.vue";
import ProspectTripSuggestion from "@/components/prospect/ProspectTripSuggestion.vue";
import TripAgenda from "@/components/prospect/trip/TripAgenda.vue";
import TripCases from "@/components/prospect/trip/TripCases.vue";
import TripReview from "@/components/prospect/trip/TripReview.vue";
import TripStops from "@/components/prospect/trip/TripStops.vue";
import TripTeams from "@/components/prospect/trip/TripTeams.vue";
import TripTravel from "@/components/prospect/trip/TripTravel.vue";
import TripWindow from "@/components/prospect/trip/TripWindow.vue";
import tripEditing from "@/components/prospect/trip/trip-editing";
import tripPlanning from "@/components/prospect/trip/trip-planning";
import tripTravel from "@/components/prospect/trip/trip-travel";
import { PALETTE, STEPS, sleep } from "@/components/prospect/trip/trip-constants";
import { errorMessage, formatFeedDate, formatHours, formatMoneyShort, notifyError, notifySuccess, TRIP_STATUS_ITEMS, UF_ITEMS } from "@/components/prospect/prospect.util";

// Prospeccao, etapa 5b: editor do roteiro de visitas, em quatro etapas (casos
// e equipes, como chegar, rota e agenda, revisar e gerar). O estado (equipes e
// paradas) fica aqui e e salvo inteiro a cada mudanca; a resposta traz o plano
// de cada equipe calculado no servidor. As partes da tela ficam em
// components/prospect/trip e usam este componente por inject ("editor").
export default {
    name: "ProspectTripEditor",

    components: { ProspectRouteMap, ProspectTripSuggestion, TripAgenda, TripCases, TripReview, TripStops, TripTeams, TripTravel, TripWindow },

    mixins: [tripPlanning, tripEditing, tripTravel],

    provide() {
        return { editor: this };
    },

    data: () => ({
        trip: null,
        loading: false,
        step: null,
        form: { name: "", status: "rascunho", uf: null, base_id: null, office_id: null, travel_mode: "car", arrival_airport_id: null, start_date: "", options: {}, notes: null },
        offices: [],
        airports: [],
        access: null,
        accessKey: "",
        modeKey: 0,
        pois: null,
        poisKey: "",
        poisLoading: false,
        poisError: null,
        suggest: { open: false, loading: false, items: [], uf: null, max_cases: 8 },
        teams: [],
        stops: [],
        activeTeamKey: null,
        agendaTab: 0,
        orderLists: {},
        dayLists: {},
        baseOptions: [],
        baseSearch: "",
        searchTimer: null,
        candidates: [],
        unresolved: [],
        candidatesLoading: false,
        municipalityOptions: {},
        caseScope: "todos",
        caseSearch: "",
        admins: [],
        saving: false,
        dirty: false,
        saveAgain: false,
        saveTimer: null,
        savedAt: null,
        saveError: null,
        applyPending: {},
        pin: { open: false, case_id: null, day: null, time: "" },
        split: { open: false, saving: false, count: 2 },
        generate: { open: false, saving: false },
        statusItems: TRIP_STATUS_ITEMS,
        ufs: UF_ITEMS,
        palette: PALETTE,
        baseCasesItems: [{ value: "end", text: "No fim, na volta" }, { value: "start", text: "No início, na chegada" }],
    }),

    computed: {
        // Etapas com o resumo do que ja foi feito em cada uma.
        steps() {
            const total = this.stops.length;
            const teams = this.teams.length;
            const plans = this.teams.map((team) => this.planOf(team)).filter(Boolean);
            const days = plans.reduce((most, plan) => Math.max(most, plan.totals.days), 0);
            const km = plans.reduce((sum, plan) => sum + plan.totals.km, 0);
            const travel = this.trip ? this.trip.travel : null;
            const generated = this.trip ? this.trip.stops.filter((stop) => stop.meeting).length : 0;
            const summaries = {
                casos: total ? `${total} ${total === 1 ? "caso" : "casos"} · ${teams} ${teams === 1 ? "equipe" : "equipes"}` : "nenhum caso ainda",
                viagem: this.form.travel_mode === "flight" && travel && travel.flight ? `avião ${travel.flight.from} → ${travel.flight.to}` : `carro${travel && travel.office ? ` desde ${travel.office.name}` : ""}`,
                rota: plans.length ? `${days} ${days === 1 ? "dia" : "dias"} · ${km.toLocaleString("pt-BR")} km` : "sem rota calculada",
                revisar: generated ? `${generated} de ${total} agendas geradas` : (this.form.start_date ? "pronto para gerar" : "falta a data de saída"),
            };
            return STEPS.map((item) => ({ ...item, summary: summaries[item.value] }));
        },

        stepIndex() {
            return Math.max(0, STEPS.findIndex((item) => item.value === this.step));
        },

        id() {
            return Number(this.$route.params.id);
        },

        saveText() {
            if (this.saveError) return `Não foi possível salvar: ${this.saveError}`;
            if (this.saving || this.dirty) return "Salvando…";
            if (this.savedAt) return `Salvo às ${this.savedAt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}. As mudanças são salvas sozinhas.`;
            return "As mudanças são salvas sozinhas.";
        },

        sourceLabel() {
            const source = this.trip && this.trip.plan ? this.trip.plan.source : null;
            if (source === "waybuilder") return "Distâncias do Waybuilder";
            if (source === "mixed") return "Tempos do Waybuilder · km estimados";
            return "Distâncias estimadas (≈)";
        },

        officeItems() {
            return this.offices.map((office) => ({ value: office.id, text: office.name }));
        },

        // Aeroportos ativos; os sugeridos para os casos do roteiro primeiro.
        airportItems() {
            const suggested = this.access ? this.access.flights.map((option) => option.airport.id) : [];
            const text = (airport) => `${airport.iata} · ${airport.city}/${airport.uf}`;
            const active = this.airports.filter((airport) => airport.active || airport.id === this.form.arrival_airport_id);
            const first = suggested.map((id) => active.find((airport) => airport.id === id)).filter(Boolean);
            const rest = active.filter((airport) => !suggested.includes(airport.id)).sort((a, b) => a.uf.localeCompare(b.uf) || a.city.localeCompare(b.city));
            return [
                ...(first.length ? [{ header: "Perto dos casos" }, ...first.map((airport) => ({ value: airport.id, text: text(airport) }))] : []),
                ...(first.length ? [{ header: "Outros" }] : []),
                ...rest.map((airport) => ({ value: airport.id, text: text(airport) })),
            ];
        },

        travelDescription() {
            const travel = this.trip ? this.trip.travel : null;
            if (this.form.travel_mode === "flight" && travel && travel.flight) {
                const flight = travel.flight;
                return `Voo ${flight.from} → ${flight.to} ${flight.direct ? "direto" : `com escala em ${flight.via}`} saindo às ${this.form.options.flight_departure || "07:00"} (≈ ${formatHours(flight.minutes)} de voo). Equipe de carro em ${travel.arrival_airport.city} às ${travel.arrival}; o dia 1 começa aí. Tempos estimados, sem consulta a companhias.`;
            }
            if (this.form.travel_mode === "flight") return "Escolha o aeroporto de chegada. A base do roteiro passa a ser a cidade do aeroporto.";
            return "Carro desde a sede, ou avião até um aeroporto perto e carro alugado a partir dali. A recomendação compara os tempos estimados.";
        },

        memberItems() {
            return this.admins.map((person) => ({ value: person.id, text: person.name }));
        },

        teamItems() {
            return this.teams.map((team) => ({ value: team.key, text: team.name, color: team.color }));
        },

        activeTeam() {
            return this.teams.find((team) => team.key === this.activeTeamKey) || this.teams[0] || null;
        },

        activeMapTeam() {
            return this.activeTeam ? { id: this.activeTeam.key, name: this.activeTeam.name, color: this.activeTeam.color } : null;
        },

        stopByCase() {
            return new Map(this.stops.map((stop) => [stop.case_id, stop]));
        },

        // Dados de cada caso: candidatos (pendentes) e os casos ja no roteiro.
        caseIndex() {
            const index = new Map();
            (this.trip ? this.trip.stops : []).forEach((stop) => {
                if (stop.case) index.set(stop.case_id, { ...stop.case, outside: true });
            });
            this.candidates.forEach((item) => index.set(item.id, { ...item, outside: false }));
            return index;
        },

        visitIndex() {
            const index = new Map();
            if (!this.trip || !this.trip.plan) return index;
            (this.trip.plan.teams || []).forEach((entry) => {
                if (entry.plan) entry.plan.visits.forEach((visit) => index.set(visit.case_id, visit));
            });
            return index;
        },

        mapBase() {
            if (this.trip && this.trip.base && this.trip.base.id === this.form.base_id) return this.trip.base;
            return this.baseOptions.find((item) => item.id === this.form.base_id) || null;
        },

        mapCities() {
            const cities = new Map();
            const rank = { alta: 0, media: 1, baixa: 2 };
            this.caseIndex.forEach((item) => {
                const city = item.municipality;
                if (!city) return;
                const stop = this.stopByCase.get(item.id);
                if (item.outside && !stop) return;
                const current = cities.get(city.id) || { ...city, priority: item.priority || "baixa", cases: [] };
                if (item.priority && rank[item.priority] < rank[current.priority]) current.priority = item.priority;
                current.cases.push({ id: item.id, company_name: item.company_name, team_id: stop ? stop.team_key : null });
                cities.set(city.id, current);
            });
            return [...cities.values()];
        },

        mapTeams() {
            return this.teams.map((team) => {
                const plan = this.planOf(team);
                return { id: team.key, name: team.name, color: team.color, order: plan ? plan.order.map((city) => city.id) : [], geometry: plan ? plan.geometry : null };
            });
        },

        mapAirports() {
            return this.airports.filter((airport) => airport.active || airport.id === this.form.arrival_airport_id);
        },

        mapOffices() {
            return this.offices.filter((office) => office.municipality).map((office) => ({ id: office.id, name: office.name, lat: office.municipality.lat, lng: office.municipality.lng }));
        },

        // Pontos de apoio de todas as equipes, com a equipe e a cidade de pernoite.
        mapPois() {
            if (!this.pois) return null;
            const hotels = [];
            const fuel = [];
            const food = [];
            const seen = new Set();
            this.pois.teams.forEach((entry) => {
                const team = this.teams.find((item) => item.id === entry.team_id);
                entry.overnight.forEach((night) => night.hotels.forEach((hotel) => {
                    if (seen.has(hotel.id)) return;
                    seen.add(hotel.id);
                    hotels.push({ ...hotel, city_name: `${night.city.name}/${night.city.uf}`, nights: night.nights });
                }));
                [["fuel", fuel], ["food", food]].forEach(([kind, list]) => entry.route[kind].forEach((item) => {
                    if (seen.has(item.id)) return;
                    seen.add(item.id);
                    list.push({ ...item, team_name: team && this.teams.length > 1 ? team.name : null });
                }));
            });
            return { hotels, fuel, food, source: this.pois.source, errors: this.pois.teams.flatMap((entry) => entry.errors) };
        },

        mapDescription() {
            const totals = this.trip && this.trip.plan ? this.trip.plan.totals : null;
            if (!totals || !totals.cases) return "Inclua casos para montar o roteiro.";
            return `${totals.cases} ${totals.cases === 1 ? "caso" : "casos"} · ${formatMoneyShort(totals.amount)} · ${totals.estimated ? "≈ " : ""}${totals.km.toLocaleString("pt-BR")} km · ${totals.days} ${totals.days === 1 ? "dia" : "dias"}${this.teams.length > 1 ? ` (equipe mais longa)` : ""}`;
        },

        mapsItems() {
            const items = [];
            this.teams.forEach((team) => {
                const plan = this.planOf(team);
                if (!plan) return;
                if (this.teams.length > 1) items.push({ header: team.name });
                items.push({ text: plan.maps_url_truncated ? "Rota inteira (até 9 paradas)" : "Rota inteira", icon: "mdi-map-marker-path", handler: () => window.open(plan.maps_url, "_blank", "noopener") });
                plan.days.filter((day) => day.maps_url).forEach((day) => items.push({ text: `Dia ${day.index}`, icon: "mdi-calendar-today", handler: () => window.open(day.maps_url, "_blank", "noopener") }));
            });
            return items;
        },

        caseScopeItems() {
            const total = this.allCases.length;
            const inside = this.allCases.filter((item) => this.inTrip(item.id)).length;
            return [
                { value: "todos", text: "Todos", count: total },
                { value: "no_roteiro", text: "No roteiro", count: inside },
                { value: "fora", text: "Fora do roteiro", count: total - inside },
            ];
        },

        allCases() {
            const rank = { alta: 0, media: 1, baixa: 2 };
            return [...this.caseIndex.values()]
                .filter((item) => !item.outside || this.stopByCase.has(item.id))
                .sort((a, b) => (a.priority in rank ? rank[a.priority] : 3) - (b.priority in rank ? rank[b.priority] : 3) || (b.amount || 0) - (a.amount || 0));
        },

        filteredCandidates() {
            const term = String(this.caseSearch || "").trim().toLowerCase();
            return this.allCases.filter((item) => {
                if (this.caseScope === "no_roteiro" && !this.inTrip(item.id)) return false;
                if (this.caseScope === "fora" && this.inTrip(item.id)) return false;
                if (!term) return true;
                return `${item.company_name} ${item.municipality ? item.municipality.name : ""}`.toLowerCase().includes(term);
            });
        },

        pinDayItems() {
            const most = Math.max(1, ...this.teams.map((team) => (this.dayLists[team.key] || []).length));
            const items = [{ value: null, text: "Automático" }];
            for (let day = 1; day <= most; day += 1) items.push({ value: day, text: `Dia ${day}${this.form.start_date ? ` (${this.dayDate(day - 1)})` : ""}` });
            return items;
        },
    },

    watch: {
        baseSearch(term) {
            clearTimeout(this.searchTimer);
            if (!term || term.length < 2) return;
            this.searchTimer = setTimeout(async () => {
                try {
                    const result = await axios.get("/api/admin/prospect/municipalities", { params: { q: term } });
                    const current = this.baseOptions.filter((item) => item.id === this.form.base_id);
                    this.baseOptions = [...current, ...result.data.content.filter((item) => item.id !== this.form.base_id)];
                } catch (error) {
                    notifyError(this, error);
                }
            }, 300);
        },

        teams(list) {
            if (this.agendaTab >= list.length) this.agendaTab = 0;
        },

        "$route.params.id"() {
            this.step = null;
            this.load();
        },

        "$route.query.etapa"(value) {
            if (STEPS.some((item) => item.value === value) && value !== this.step) this.step = value;
        },
    },

    created() {
        this.load();
        this.loadAdmins();
        this.loadTravelData();
    },

    beforeRouteLeave(to, from, next) {
        this.flush().finally(() => next());
    },

    methods: {
        formatFeedDate,
        formatMoneyShort,

        cityText(city) {
            return city ? `${city.name}/${city.uf}` : "";
        },

        priorityLabel(priority) {
            return { alta: "Alta", media: "Média", baixa: "Baixa" }[priority];
        },

        priorityBadge(priority) {
            return { alta: "rejected", media: "pending", baixa: "inactive" }[priority];
        },

        // ---------------------------------------------------------------
        // Carga e salvamento
        // ---------------------------------------------------------------
        async load() {
            this.loading = true;
            try {
                const result = await axios.get(`/api/admin/prospect/trips/${this.id}`);
                this.applyServer(result.data.content, { full: true });
                // Etapa inicial: a da URL; senao, casos (roteiro vazio) ou rota.
                if (!this.step) {
                    const wanted = this.$route.query.etapa;
                    this.step = STEPS.some((item) => item.value === wanted) ? wanted : (this.stops.length ? "rota" : "casos");
                }
                await this.loadCandidates();
            } catch (error) {
                notifyError(this, error);
                if (error.response && error.response.status === 404) this.$router.replace("/prospeccao/rota");
            } finally {
                this.loading = false;
            }
        },

        async loadAdmins() {
            try {
                const result = await axios.get("/api/admin/prospect/team");
                this.admins = result.data.content;
            } catch (error) {
                notifyError(this, error);
            }
        },

        async loadCandidates() {
            this.candidatesLoading = true;
            try {
                const params = {};
                if (this.form.uf) params.uf = this.form.uf;
                if (this.form.base_id) params.base_id = this.form.base_id;
                const result = await axios.get("/api/admin/prospect/route/candidates", { params });
                const content = result.data.content;
                this.candidates = content.cases;
                this.unresolved = content.unresolved;
                // Sem base ainda: usa a sugerida (capital da UF com mais casos).
                if (!this.form.base_id && content.base) {
                    this.baseOptions = [content.base];
                    this.form.base_id = content.base.id;
                    this.queueSave();
                    this.loadCandidates();
                }
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.candidatesLoading = false;
            }
        },

        // Copia o estado do servidor. full: substitui equipes e paradas locais
        // (sem mudancas pendentes); senao so liga ids das equipes novas.
        applyServer(trip, { full = false, sentTeams = null } = {}) {
            if (sentTeams) {
                sentTeams.forEach((sent, index) => {
                    const local = this.teams.find((team) => team.key === sent.key);
                    if (local && !local.id && trip.teams[index]) local.id = trip.teams[index].id;
                });
            }
            this.trip = trip;
            if (full) {
                const keyOf = (id) => {
                    const local = this.teams.find((team) => team.id === id);
                    return local ? local.key : `t${id}`;
                };
                this.form = {
                    name: trip.name,
                    status: trip.status,
                    uf: trip.uf,
                    base_id: trip.base ? trip.base.id : null,
                    office_id: trip.office_id || (trip.travel && trip.travel.office ? trip.travel.office.id : null),
                    travel_mode: trip.travel_mode || "car",
                    arrival_airport_id: trip.arrival_airport_id || null,
                    start_date: trip.start_date || "",
                    options: { ...trip.options },
                    notes: trip.notes,
                };
                if (trip.base && !this.baseOptions.some((item) => item.id === trip.base.id)) this.baseOptions = [trip.base, ...this.baseOptions];
                this.teams = trip.teams.map((team) => ({
                    key: keyOf(team.id),
                    id: team.id,
                    name: team.name,
                    color: team.color,
                    member_ids: team.member_ids.slice(),
                    locked_days: team.locked_days.slice(),
                    manual_order: team.manual_order,
                }));
                this.stops = trip.stops.map((stop) => ({
                    case_id: stop.case_id,
                    team_key: keyOf(stop.team_id),
                    position: stop.position,
                    pin_day: stop.pin_day,
                    pin_time: stop.pin_time,
                    locked: stop.locked,
                }));
                if (!this.teams.some((team) => team.key === this.activeTeamKey)) this.activeTeamKey = this.teams[0] ? this.teams[0].key : null;
            }
            this.rebuildLists();
            this.refreshAccess();
            // Roteiro mudou: os pontos de apoio carregados ficam velhos.
            if (this.pois && this.poisKey !== this.poisSignature()) {
                this.pois = null;
                this.poisError = null;
            }
        },

        payload() {
            const teamById = (key) => this.teams.find((team) => team.key === key);
            return {
                name: this.form.name,
                status: this.form.status,
                uf: this.form.uf || null,
                base_id: this.form.base_id || null,
                office_id: this.form.office_id || null,
                travel_mode: this.form.travel_mode,
                arrival_airport_id: this.form.travel_mode === "flight" ? this.form.arrival_airport_id || null : null,
                start_date: this.form.start_date || null,
                options: { ...this.form.options, visit_hours: Number(this.form.options.visit_hours) || 1 },
                notes: this.form.notes,
                teams: this.teams.map((team) => ({
                    id: team.id || undefined,
                    key: team.key,
                    name: team.name,
                    color: team.color,
                    member_ids: team.member_ids,
                    locked_days: team.locked_days,
                    manual_order: team.manual_order,
                })),
                stops: this.stops.map((stop) => {
                    const team = teamById(stop.team_key);
                    return {
                        case_id: stop.case_id,
                        team_id: team && team.id ? team.id : undefined,
                        team_key: stop.team_key,
                        position: stop.position,
                        pin_day: stop.pin_day,
                        pin_time: stop.pin_time,
                        locked: stop.locked,
                    };
                }),
            };
        },

        queueSave() {
            this.dirty = true;
            clearTimeout(this.saveTimer);
            this.saveTimer = setTimeout(() => this.save(), 600);
        },

        async save() {
            if (this.saving) {
                this.saveAgain = true;
                return;
            }
            clearTimeout(this.saveTimer);
            if (!this.form.name || !String(this.form.name).trim()) {
                this.saveError = "dê um nome ao roteiro.";
                this.dirty = false;
                return;
            }
            this.saving = true;
            this.dirty = false;
            const payload = this.payload();
            try {
                const result = await axios.put(`/api/admin/prospect/trips/${this.id}`, payload);
                this.applyServer(result.data.content, { sentTeams: payload.teams, full: !this.dirty && !this.saveAgain });
                this.savedAt = new Date();
                this.saveError = null;
            } catch (error) {
                this.saveError = errorMessage(error);
                notifyError(this, error);
            } finally {
                this.saving = false;
                if (this.saveAgain || this.dirty) {
                    this.saveAgain = false;
                    this.queueSave();
                }
            }
        },

        // Grava o que estiver pendente antes de uma acao no servidor.
        async flush() {
            clearTimeout(this.saveTimer);
            while (this.saving) await sleep(80);
            if (this.dirty) await this.save();
            while (this.saving) await sleep(80);
        },

        // ---------------------------------------------------------------
        // Dados do roteiro
        // ---------------------------------------------------------------
        onUfChange() {
            this.queueSave();
            this.loadCandidates();
        },

        onBaseChange() {
            this.queueSave();
            this.loadCandidates();
        },

        async searchFor(item, term) {
            if (!term || term.length < 2) return;
            try {
                const result = await axios.get("/api/admin/prospect/municipalities", { params: { q: term, uf: item.uf } });
                this.$set(this.municipalityOptions, item.id, result.data.content);
            } catch (error) {
                notifyError(this, error);
            }
        },

        async setMunicipality(item, municipalityId) {
            if (!municipalityId) return;
            try {
                await axios.patch(`/api/admin/prospect/cases/${item.id}`, { municipality_id: municipalityId });
                notifySuccess(this, "Município definido.");
                await this.loadCandidates();
            } catch (error) {
                notifyError(this, error);
            }
        },

        setStep(value) {
            this.step = value;
            if (this.$route.query.etapa !== value) this.$router.replace({ query: { ...this.$route.query, etapa: value } });
            window.scrollTo({ top: 0, behavior: "smooth" });
        },

        openGenerate() {
            this.setStep("revisar");
        },

        async submitGenerate() {
            this.generate.saving = true;
            try {
                await this.flush();
                const result = await axios.post(`/api/admin/prospect/trips/${this.id}/meetings`);
                const { created, updated, kept, trip } = result.data.content;
                this.applyServer(trip, { full: true });
                const parts = [];
                if (created) parts.push(`${created} ${created === 1 ? "agenda criada" : "agendas criadas"}`);
                if (updated) parts.push(`${updated} ${updated === 1 ? "atualizada" : "atualizadas"}`);
                if (kept) parts.push(`${kept} já ${kept === 1 ? "realizada" : "realizadas"}`);
                notifySuccess(this, `${parts.join(", ") || "Nada a gerar"}.`);
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.generate.saving = false;
            }
        },
    },
};
</script>

<!-- Sem scoped: as classes pe-* valem tambem para as partes em components/prospect/trip. -->
<style lang="scss">
@import "../../components/prospect/trip/trip-editor";
</style>
